// 약관/개인정보 동의 화면.
// 작업계획서 4.1 + docs/security-checklist.md §8: 필수/선택 분리, 만 14세 미만 가입 제한.
// 본 화면은 휴대폰 OTP 경로(verificationToken) 또는 소셜 콜백 경로(linkToken) 양쪽에서 진입한다.
// "동의하고 가입" 클릭 시 1-G 에서 추가될 /auth/signup-or-login 라우트를 호출하도록 연결한다 (현재는 콘솔 로그 + 홈 이동 placeholder).

import { useState } from "react";
import { useLocation, useNavigate, Navigate } from "react-router-dom";
import { signupOrLogin } from "@/features/auth/authApi.ts";
import { ApiError } from "@/api/client.ts";
import { useAuthStore } from "@/stores/authStore.ts";
import { Button } from "@/shared/ui";
import { TermsContent } from "@/pages/legal/TermsPage.tsx";
import { PrivacyContent } from "@/pages/legal/PrivacyPage.tsx";

// 약관 보기 모달에서 보여줄 정책 종류.
type PolicyKind = "terms" | "privacy";

interface PhoneSource {
  source: "phone";
  phone: string;
  verificationToken: string;
  // OTP 단계에서 입력한 이름. 신규 가입 시 회원 이름으로 저장된다.
  displayName?: string;
}

interface SocialSource {
  source: "social";
  provider: "kakao" | "naver" | "google";
  linkToken: string;
  // 소셜 경로도 휴대폰 OTP 를 거친다(정책). 그 결과 토큰을 함께 제출한다.
  verificationToken: string;
  phone: string;
  // OTP 단계에서 입력한 이름. 소셜 닉네임보다 우선해 회원 이름으로 저장된다.
  displayName?: string;
}

type LocationState = PhoneSource | SocialSource;

interface ConsentsState {
  terms: boolean;
  privacy: boolean;
  over14: boolean;
  marketingPush: boolean;
  marketingSms: boolean;
}

const REQUIRED_KEYS = ["terms", "privacy", "over14"] as const;

// 약관 동의 화면 컴포넌트.
// 직접 URL 진입 차단을 위해 location.state 가 없으면 /login 으로 보낸다.
export default function ConsentsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const state = (location.state ?? null) as LocationState | null;

  const [agreed, setAgreed] = useState<ConsentsState>({
    terms: false,
    privacy: false,
    over14: false,
    marketingPush: false,
    marketingSms: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // 약관/방침 전문 보기 모달(새 탭 대신 인앱 모달 - 스플래시 재노출 회피).
  const [policyModal, setPolicyModal] = useState<PolicyKind | null>(null);
  const setSession = useAuthStore((s) => s.setSession);

  if (!state) {
    return <Navigate to="/login" replace />;
  }

  const allRequiredOk = REQUIRED_KEYS.every((k) => agreed[k]);
  const allChecked = (["terms", "privacy", "over14", "marketingPush", "marketingSms"] as const).every(
    (k) => agreed[k],
  );

  function toggleAll(value: boolean) {
    setAgreed({
      terms: value,
      privacy: value,
      over14: value,
      marketingPush: value,
      marketingSms: value,
    });
  }

  function toggle(key: keyof ConsentsState) {
    setAgreed((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    // 콜백 내부에서는 외부의 narrowing 이 풀리므로 state 를 다시 한 번 검사한다.
    if (!state || !allRequiredOk) {
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      // 휴대폰/소셜 두 경로 모두 휴대폰 OTP(verificationToken)를 거친다. 소셜은 linkToken 을 추가로 제출한다.
      // OTP 단계에서 입력한 이름(displayName)을 회원 이름으로 함께 전달한다.
      const result =
        state.source === "phone"
          ? await signupOrLogin({
              source: "phone",
              verificationToken: state.verificationToken,
              displayName: state.displayName,
              consents: agreed,
            })
          : await signupOrLogin({
              source: "social",
              linkToken: state.linkToken,
              verificationToken: state.verificationToken,
              displayName: state.displayName,
              consents: agreed,
            });
      setSession({
        accessToken: result.accessToken,
        refreshToken: result.refreshToken,
        expiresAtSec: result.expiresAtSec,
        user: {
          id: result.user.id,
          displayName: result.user.displayName,
          tier: result.user.tier,
        },
      });
      navigate("/", { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("일시적인 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="mx-auto flex min-h-dvh max-w-screen-sm flex-col gap-6 px-6 py-10">
      <header className="flex flex-col gap-2">
        <h1 className="text-2xl font-bold">서비스 이용 동의</h1>
        <p className="text-base text-ink-600">필수 항목에 동의하셔야 가입을 완료하실 수 있습니다.</p>
      </header>

      <form onSubmit={onSubmit} className="flex flex-col gap-3">
        <label className="flex items-center gap-3 border-b border-ink-200 pb-3">
          <input
            type="checkbox"
            checked={allChecked}
            onChange={(e) => toggleAll(e.target.checked)}
            className="h-5 w-5"
          />
          <span className="text-base font-medium">모두 동의</span>
        </label>

        <Row
          checked={agreed.terms}
          onChange={() => toggle("terms")}
          required
          label="이용약관 동의"
          onView={() => setPolicyModal("terms")}
        />
        <Row
          checked={agreed.privacy}
          onChange={() => toggle("privacy")}
          required
          label="개인정보 처리방침 동의"
          onView={() => setPolicyModal("privacy")}
        />
        <Row
          checked={agreed.over14}
          onChange={() => toggle("over14")}
          required
          label="만 14세 이상입니다"
        />
        <Row
          checked={agreed.marketingPush}
          onChange={() => toggle("marketingPush")}
          label="이벤트/혜택 알림 수신 (선택)"
        />
        <Row
          checked={agreed.marketingSms}
          onChange={() => toggle("marketingSms")}
          label="이벤트/혜택 SMS 수신 (선택)"
        />

        {error ? <p className="text-sm text-danger-700">{error}</p> : null}

        <Button
          type="submit"
          fullWidth
          className="mt-4"
          disabled={!allRequiredOk}
          loading={submitting}
        >
          {submitting ? "처리 중..." : "동의하고 시작하기"}
        </Button>
      </form>

      {/* 약관/방침 전문 보기 모달(인앱). 새 탭으로 나가지 않아 스플래시가 다시 뜨지 않는다. */}
      {policyModal ? (
        <PolicyModal kind={policyModal} onClose={() => setPolicyModal(null)} />
      ) : null}
    </main>
  );
}

// 약관/개인정보 전문을 보여주는 모달. 종류에 따라 본문 컴포넌트를 렌더한다.
function PolicyModal({ kind, onClose }: { kind: PolicyKind; onClose: () => void }) {
  const title = kind === "terms" ? "이용약관" : "개인정보처리방침";
  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-ink-900/50 p-0 sm:items-center sm:p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="flex max-h-[85vh] w-full max-w-screen-sm flex-col rounded-t-2xl bg-surface pb-[var(--safe-bottom)] sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-ink-200 px-5 py-3">
          <h2 className="text-base font-bold text-ink-900">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-ink-300 px-2 py-1 text-sm text-ink-700 hover:bg-ink-50"
          >
            닫기
          </button>
        </div>
        {/* 스크롤 본문 */}
        <div className="flex flex-col gap-4 overflow-y-auto px-5 py-4 text-sm leading-relaxed text-ink-800">
          {kind === "terms" ? <TermsContent /> : <PrivacyContent />}
        </div>
      </div>
    </div>
  );
}

interface RowProps {
  checked: boolean;
  onChange: () => void;
  label: string;
  required?: boolean;
  // 약관/방침 전문 보기(있으면 "보기" 버튼으로 인앱 모달을 연다 - 새 탭 미사용).
  onView?: () => void;
}

// 동의 항목 한 줄을 표시한다.
// 필수 항목은 라벨 옆에 "(필수)" 텍스트로 명시한다 (이모지 금지 + 큰 글씨 정책).
function Row({ checked, onChange, label, required, onView }: RowProps) {
  return (
    <div className="flex items-center justify-between gap-3 py-1">
      <label className="flex items-center gap-3">
        <input type="checkbox" checked={checked} onChange={onChange} className="h-5 w-5" />
        <span className="text-base">
          {label} {required ? <span className="text-danger-700">(필수)</span> : null}
        </span>
      </label>
      {onView ? (
        <button
          type="button"
          onClick={onView}
          className="shrink-0 text-sm text-ink-500 underline"
        >
          보기
        </button>
      ) : null}
    </div>
  );
}
